import { useUserSession } from '@solinkcorp/authentication';
import { type ScheduledActivityEntity } from 'clients/CallhomeApiTypes';
import moment from 'moment';
import React from 'react';
import { type KeyedMutator } from 'swr';
import FeaturesUtil from 'utils/FeaturesUtil';
import { DEFAULT_TIMEZONE, OPEN_AI_SUBTYPE } from 'vd-constants';
import ScheduledActivityForm, { type ScheduledEventFormSource } from './ScheduledActivityForm';
import { type ScheduledEventFormProps } from './useScheduledEventForm';
import useSpotCheckUsage from './useSpotCheckUsage';

interface EditScheduledSpotCheckWrapperProps {
  scheduledEventFormProps: ScheduledEventFormProps;
  scheduledActivity: ScheduledActivityEntity;
  invalidateScheduledActivity: KeyedMutator<ScheduledActivityEntity | undefined>;
  timezoneId?: string;
  source: ScheduledEventFormSource;
  analyticsSource?: string;
}

/**
 * Thin wrapper around the ScheduledActivityForm component that is used to isolate logic specific to editing scheduled Spot Checks, e.g
 * querying for usage stats from cloud AI serverless
 */
const EditScheduledSpotCheckWrapper: React.FC<EditScheduledSpotCheckWrapperProps> = ({
  scheduledEventFormProps,
  scheduledActivity,
  timezoneId,
  invalidateScheduledActivity,
  source,
  analyticsSource,
}) => {
  const { clientSettings } = useUserSession();
  const usageStats = useSpotCheckUsage(true);

  const enforceReason = FeaturesUtil.canCreateScheduledAndEventSpotChecks(clientSettings)
    ? OPEN_AI_SUBTYPE
    : undefined;

  return (
    <ScheduledActivityForm
      {...scheduledEventFormProps}
      enforceReason={enforceReason}
      activity={scheduledActivity}
      title={scheduledActivity.title}
      maxDate={moment
        .tz(timezoneId ?? DEFAULT_TIMEZONE)
        .add(2, 'year')
        .toDate()}
      source={source}
      updateActivity={invalidateScheduledActivity}
      showCloseAction={false}
      analyticsSource={analyticsSource}
      canEditOriginalCameras={true}
      viewOnly={true}
      spotCheckUsageStats={usageStats}
    />
  );
};

export default EditScheduledSpotCheckWrapper;
